import React from 'react';
import { motion } from 'framer-motion';
import { Sparkle, VideoCamera, Stack, Lightning, Warning } from '@phosphor-icons/react';

// Individual Usage Meter Component
const UsageMeter = ({ 
  icon: Icon, 
  label, 
  used, 
  limit, 
  color = 'blue',
  showWarning = false,
  warningThreshold = 0.8 
}) => {
  const percentage = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isNearLimit = percentage >= warningThreshold * 100;
  const isAtLimit = used >= limit;

  const colorClasses = {
    blue: {
      bg: 'bg-blue-500/20',
      fill: 'bg-gradient-to-r from-blue-500 to-blue-600',
      text: 'text-blue-400',
      icon: 'text-blue-400'
    },
    purple: {
      bg: 'bg-purple-500/20',
      fill: 'bg-gradient-to-r from-purple-500 to-purple-600',
      text: 'text-purple-400',
      icon: 'text-purple-400'
    },
    green: {
      bg: 'bg-green-500/20',
      fill: 'bg-gradient-to-r from-green-500 to-green-600',
      text: 'text-green-400',
      icon: 'text-green-400'
    },
    amber: {
      bg: 'bg-amber-500/20',
      fill: 'bg-gradient-to-r from-amber-500 to-amber-600',
      text: 'text-amber-400',
      icon: 'text-amber-400'
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <div className="p-3 rounded-lg bg-white/5 border border-white/10">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Icon size={16} className={colors.icon} weight="fill" />
          <span className="text-slate-300 text-sm font-medium">{label}</span>
        </div>
        <div className="flex items-center space-x-1">
          {isAtLimit && showWarning && (
            <Warning size={14} className="text-red-400" weight="fill" />
          )}
          <span className={`text-sm font-medium ${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : colors.text}`}>
            {used} / {limit}
          </span>
        </div>
      </div>
      <div className={`h-2 rounded-full ${colors.bg} overflow-hidden`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`h-full rounded-full ${isAtLimit ? 'bg-gradient-to-r from-red-500 to-red-600' : isNearLimit ? 'bg-gradient-to-r from-amber-500 to-amber-600' : colors.fill}`}
        />
      </div>
    </div>
  );
};

// Dashboard Usage Meters Component
const UsageMeters = ({ billingInfo, compact = false }) => {
  if (!billingInfo) return null;

  const {
    is_trial = false,
    trial_days_remaining = 0,
    trial_expired = false,
  } = billingInfo;

  if (compact) {
    // Round 218A Phase 8A — compact header widget no longer shows a
    // per-day AI counter. Strategy is unlimited. The Business Credits
    // balance widget lands in Phase 8B — until then we only surface
    // the trial-remaining chip when the account is on trial.
    return (
      <div className="flex items-center space-x-4 text-sm">
        <div className="flex items-center space-x-1.5">
          <VideoCamera size={14} className="text-emerald-400" weight="fill" />
          <span className="text-emerald-400 font-medium">Unlimited Strategy</span>
        </div>
        {is_trial && !trial_expired && (
          <div className="flex items-center space-x-1.5 px-2 py-0.5 bg-amber-500/20 rounded-full">
            <Lightning size={12} className="text-amber-400" weight="fill" />
            <span className="text-amber-400 font-medium">{trial_days_remaining}d trial</span>
          </div>
        )}
      </div>
    );
  }

  // Full version for dashboard body
  return (
    <div className="space-y-3">
      {/* Trial Banner */}
      {is_trial && (
        <div className={`p-3 rounded-lg ${trial_expired ? 'bg-red-500/20 border border-red-500/30' : 'bg-amber-500/20 border border-amber-500/30'}`}>
          <div className="flex items-center space-x-2">
            <Lightning size={18} className={trial_expired ? 'text-red-400' : 'text-amber-400'} weight="fill" />
            <span className={`font-medium ${trial_expired ? 'text-red-400' : 'text-amber-400'}`}>
              {trial_expired 
                ? 'Trial ended — Choose a plan to continue' 
                : `${trial_days_remaining} days left in your trial`
              }
            </span>
          </div>
        </div>
      )}

      {/* Unlimited Strategy tile — Business Credits widget lands in Phase 8B */}
      <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Sparkle size={16} className="text-emerald-400" weight="fill" />
            <span className="text-emerald-400 text-sm font-medium">Unlimited Strategy</span>
          </div>
          <span className="text-emerald-300 text-sm font-bold">Included</span>
        </div>
      </div>
    </div>
  );
};

export default UsageMeters;
export { UsageMeter };
